import React, { useEffect, useReducer, createContext, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import { SpinnerCircular } from "spinners-react";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import parse from "html-react-parser";

import RegistrationBG from "../../../assets/media/background/registration-bg.png";
import "../../../assets/css/library/bootstrap.min.css";
import "../../../assets/css/user_authentication_complete.css";
import "../../../assets/css/utility/input-field.css";

import {
  UPDATE_PROFILE_URL,
  SHIPPER_PROFILE_URL,
  CARRIER_PROFILE_URL,
} from "../../../utils/api-ends";
import useHttp from "../../../hooks/useHttp";
import BasicInfo from "./BasicInfo";
import BankInfo from "./BankInfo";
import CarrierInfo from "./CarrierInfo";
import ShipperInfo from "./ShipperInfo";
import CardInfo from "./CardInfo";

export const ManageCompleteUserProfileContext = createContext();

const initialUserInfoState = {
  stepNumber: 1,
  userInfo: {
    Inc_deductible: " ",
    agent_phone: "",
    cargo_Inc_agent: "",
    cargo_Inc_limit: "",
    cargo_insurance_company: "",
    carrier_type: "",
    driving_licence: "",
    icc_mc_number: "",
    vehicle_image: "",
    vehicle_image_url: "",
    liability_Inc_limit: "",
    load_type: "",
    license_number: "",
    bonding_agent: "",
    bonding_co_phone_number: "",
    shipper_type: "",
    user_obj: {
      bank_account_branch_name: "",
      bank_account_country: "",
      bank_account_holder_name: "",
      bank_account_number: "",
      bank_name: "",
      city: "",
      country: "",
      company_name: "",
      email: "",
      first_name: "",
      id: "",
      last_name: "",
      phone_number: "",
      zip_code: "",
      street_address: "",
      profile_picture: "",
      profile_picture_file: "",
      profile_picture_default_url: "",
      state: "",
      username: "",

      // Image Crop Detail
      profile_image_screen_width: "",
      profile_image_screen_height: "",
      profile_image_x: "",
      profile_image_y: "",
      profile_image_width: "",
      profile_image_height: "",
    },
  },
  userInfoError: {
    email: [],
    first_name: [],
    last_name: [],
    phone_number: [],
    country: [],
    state: [],
    city: [],
    zip_code: [],
    street_address: [],
    bank_name: [],
    bank_account_number: [],
    bank_account_holder_name: [],
    bank_account_branch_name: [],
    bank_account_country: [],
    company_name: [],
    license_number: [],
    bonding_agent: [],
    bonding_co_phone_number: [],
    // profile_picture: [],
    load_type: [],
    carrier_type: [],
    iccMcNumber: [],
    cargo_insurance_company: [],
    cargo_inc_limit: [],
    liability_inc_limit: [],
    cargo_inc_agent: [],
    vehicle_image: [],
    agent_phone: [],
    Inc_deductible: [],
    driving_licence: [],
    general: [],
    message: [],
    shipper_type: [],
  },
  userInfoSuccessMessage: "",
  countryList: [],
  stateList: [],
  cityList: [],
};

const reducer = (state, action) => {
  switch (action.type) {
    case "ON_CHANGE_STEP":
      return {
        ...state,
        stepNumber: action.payload,
      };
    case "USER_INFO":
      return {
        ...state,
        userInfo: {
          ...state.userInfo,
          ...action.payload.data.data,
          user_obj: {
            ...action.payload.data.data.user_obj,
            phone_number: action.payload.data.data?.user_obj?.phone_number
              ? action.payload.data.data?.user_obj?.phone_number?.split("+1")[1]
              : "",
          },
        },
      };
    case "ON_CHANGE":
      return {
        ...state,
        userInfo: {
          ...state.userInfo,
          ...action.payload,
        },
      };
    case "ON_CHANGE_USER_INFO":
      return {
        ...state,
        userInfo: {
          ...state.userInfo,
          user_obj: {
            ...state.userInfo.user_obj,
            ...action.payload,
          },
        },
      };
    case "MANAGE_ERROR_MESSAGES":
      return {
        ...state,
        userInfoError: {
          ...state.userInfoError,
          ...action.payload?.error,
        },
      };
    case "MANAGE_FIELD_RELATED_ERROR_MESSAGES":
      return {
        ...state,
        userInfoError: {
          ...state.userInfoError,
          [action.payload.name]: action.payload.value,
        },
      };
    case "CLEAR_ERROR_MESSAGES":
      return {
        ...state,
        userInfoError: {
          ...state.userInfoError,
          ...action.payload,
        },
      };
    case "CLEAR_ALL_ERROR_MESSAGES":
      return {
        ...state,
        userInfoError: {
          email: [],
          first_name: [],
          last_name: [],
          phone_number: [],
          country: [],
          state: [],
          city: [],
          zip_code: [],
          street_address: [],
          bank_name: [],
          bank_account_number: [],
          bank_account_holder_name: [],
          bank_account_branch_name: [],
          bank_account_country: [],
          company_name: [],
          license_number: [],
          bonding_agent: [],
          bonding_co_phone_number: [],
          profile_picture: [],
          load_type: [],
          carrier_type: [],
          iccMcNumber: [],
          vehicle_image: [],
          cargo_insurance_company: [],
          cargo_inc_limit: [],
          liability_inc_limit: [],
          cargo_inc_agent: [],
          agent_phone: [],
          Inc_deductible: [],
          driving_licence: [],
          general: [],
          message: [],
          shipper_type: [],
        },
      };
    case "SUCCESS_MESSAGES":
      return {
        ...state,
        userInfoSuccessMessage: action.payload?.data?.message,
      };
    case "CLEAR_SUCCESS_MESSAGES":
      return {
        ...state,
        userInfoSuccessMessage: "",
      };
    case "COUNTRY_LIST":
      return {
        ...state,
        countryList: action.payload,
      };
    case "STATE_LIST":
      return {
        ...state,
        stateList: action.payload,
      };
    case "CITY_LIST":
      return {
        ...state,
        cityList: action.payload,
      };
    default:
      return state;
  }
};

const CompleteUserProfile = () => {
  const [userInfoState, userInfoContextDispatch] = useReducer(
    reducer,
    initialUserInfoState
  );
  const { error, isLoading, sendRequests } = useHttp();
  const navigate = useNavigate();
  const userData = useSelector((state) => state.basicInfo?.userDetails);
  const userInfo = userInfoState.userInfo;

  const step1Fields = [
    "first_name",
    "last_name",
    "last_name",
    "email",
    "phone_number",
    "state",
    "city",
    "zip_code",
    "street_address",
    "profileFileError",
    "profile_picture",
  ];
  const step1RequiredFields = [...step1Fields];
  const step2CarrierFields = [
    "carrier_type",
    "cargo_insurance_company",
    "cargo_Inc_limit",
    "liability_Inc_limit",
    "agent_phone",
    "Inc_deductible",
    "cargo_Inc_agent",
    "driving_licence",
    "vehicle_image: "
  ];
  const step2ShipperFields = [
    "shipper_type",
    "bonding_agent",
    "bonding_co_phone_number",
    "license_number",
    "company_name",
    // "vehicle_image",
    // "vehicle_image_url"
  ];
  const step2CarrierRequiredFields = [...step2CarrierFields];
  const step2ShipperRequiredFields = [...step2ShipperFields];
  const step3Fields = [
    "bank_name",
    "bank_account_number",
    "bank_account_holder_name",
    "bank_account_branch_name",
    "bank_account_country",
  ];
  const step3RequiredFields = [...step3Fields];

  // Fetch user profile details
  useEffect(() => {
    let url = CARRIER_PROFILE_URL;
    if (userData?.user_type === "shipper") {
      url = SHIPPER_PROFILE_URL;
    }

    const response = (data) => {
      userInfoContextDispatch({
        type: "USER_INFO",
        payload: { data },
      });
    };

    sendRequests(
      {
        url: url,
        method: "GET",
      },
      response
    );
  }, [userData?.user_type]);

  const handleSubmitData = (e) => {
    if (isLoading) {
      return false;
    }

    // Clear Messages
    userInfoContextDispatch({
      type: "CLEAR_ALL_ERROR_MESSAGES",
    });
    userInfoContextDispatch({
      type: "CLEAR_SUCCESS_MESSAGES",
      payload: {},
    });

    // Validate Data
    if (hasEmptyFieldStep3()) return;

    const formData = new FormData();
    // Set FormData values userInfoState.userInfo
    const excludeFields = [
      "profile_picture",
      "profile_picture_default_url",
      "profile_picture_file",
      "balance",
      "vehicle_image_url"
    ];

    for (var key in userInfo) {
      if (!["", null, undefined].includes(userInfo[key])) {
        if (key === "user_obj") {
          for (var u_key in userInfo["user_obj"]) {
            if (
              userInfoState.userInfo["user_obj"][u_key] !== null &&
              !excludeFields.includes(u_key)
            ) {
              formData.append(u_key, userInfo["user_obj"][u_key]);
            }

            if (
              !["", null, undefined].includes(
                userInfoState.userInfo["user_obj"][u_key]
              ) &&
              u_key === "profile_picture_file"
            ) {
              formData.append(
                "profile_picture",
                userInfo["user_obj"]["profile_picture_file"]
              );
            }
            if (
              u_key === "phone_number" &&
              userInfoState.userInfo["user_obj"][u_key]?.length > 0
            ) {
              formData.append(
                "phone_number",
                `+1${userInfoState.userInfo["user_obj"][u_key]}`
              );
            }
          }
        } else {
          if (
            ["agent_phone", "bonding_co_phone_number"].includes(key) &&
            userInfoState.userInfo[key]?.length > 0
          ) {
            formData.append(key, `+1${userInfoState.userInfo[key]}`);
          } else {
            formData.append(key, userInfoState.userInfo[key]);
          }
        }
      }
    }

    const response = (data) => {
      // Set success messages
      userInfoContextDispatch({
        type: "SUCCESS_MESSAGES",
        payload: { data },
      });

      // Clear success messages
      setTimeout(() => {
        userInfoContextDispatch({
          type: "CLEAR_SUCCESS_MESSAGES",
          payload: {},
        });
      }, 3000);

      // Navigate to email verification or phone verification page
      setTimeout(() => {
        data.email_update === true && data.phone_number_update === true
          ? navigate("/auth/account-verification?to_verify=both")
          : data.email_update === true
          ? navigate("/auth/account-verification?to_verify=email")
          : data.phone_number_update === true
          ? navigate("/auth/account-verification?to_verify=phone")
          : navigate("/profile-information/");
      }, 1000);
    };

    // Send request to update profile
    sendRequests(
      {
        url: UPDATE_PROFILE_URL,
        method: "PATCH",
        headers: {
          "Content-Type": "multipart/form-data",
        },
        data: formData,
      },
      response
    );
  };

  useEffect(() => {
    // set error message in state
    if (error) {
      userInfoContextDispatch({
        type: "MANAGE_ERROR_MESSAGES",
        payload: { error },
      });

      // Check error and change step number according to error fields
      Object.keys(error).forEach(function (key) {
        if (step1Fields.includes(key)) {
          userInfoContextDispatch({
            type: "ON_CHANGE_STEP",
            payload: 1,
          });
        } else if (
          [...step2CarrierFields, ...step2ShipperFields].includes(key)
        ) {
          userInfoContextDispatch({
            type: "ON_CHANGE_STEP",
            payload: 2,
          });
        } else if (step3Fields.includes(key)) {
          userInfoContextDispatch({
            type: "ON_CHANGE_STEP",
            payload: 3,
          });
        }
      });
    }
  }, [error]);

  const hasEmptyFieldStep1 = () => {
    let hasErrorStep1 = false;
    // Check step 1 field is empty or not, if empty then through error
    Object.keys(userInfo.user_obj).forEach(function (key) {
      if (["email", "phone_number", "profile_picture"].includes(key)) {
        return;
      }

      if (
        userInfoState.stepNumber === 1 &&
        step1RequiredFields.includes(key) &&
        ["", null, undefined].includes(userInfo.user_obj[key])
      ) {
        userInfoContextDispatch({
          type: "MANAGE_FIELD_RELATED_ERROR_MESSAGES",
          payload: {
            name: key,
            value: ["This field is required."],
          },
        });
        hasErrorStep1 = true;
      }
    });

    // Phone Number and Email one of them is required
    if (
      ["", null].includes(userInfo.user_obj["email"]) &&
      ["", null].includes(userInfo.user_obj["phone_number"])
    ) {
      userInfoContextDispatch({
        type: "MANAGE_FIELD_RELATED_ERROR_MESSAGES",
        payload: {
          name: "email",
          value: ["This field is required."],
        },
      });
      hasErrorStep1 = true;
    }
    return hasErrorStep1;
  };

  const hasEmptyFieldStep2 = () => {
    // Check step 2 field is empty or not, if empty then through error
    let hasErrorStep2 = false;
    const step2Fields =
      userData?.user_type === "shipper"
        ? step2ShipperRequiredFields
        : step2CarrierRequiredFields;

    Object.keys(userInfo).forEach(function (key) {
      if (
        userInfoState.stepNumber === 2 &&
        step2Fields.includes(key) &&
        ["", null, undefined].includes(userInfo[key])
      ) {
        userInfoContextDispatch({
          type: "MANAGE_FIELD_RELATED_ERROR_MESSAGES",
          payload: {
            name: key,
            value: ["This field is required."],
          },
        });
        hasErrorStep2 = true;
      }
    });

    // Check company name cause company is under user object
    if (["", null, undefined].includes(userInfo.user_obj["company_name"])) {
      userInfoContextDispatch({
        type: "MANAGE_FIELD_RELATED_ERROR_MESSAGES",
        payload: {
          name: "company_name",
          value: ["This field is required."],
        },
      });
      hasErrorStep2 = true;
    }

    console.log(hasErrorStep2, step2Fields)

    return hasErrorStep2;
  };

  const hasEmptyFieldStep3 = () => {
    let hasErrorStep3 = false;
    // Check step 1 field is empty or not, if empty then through error
    Object.keys(userInfo.user_obj).forEach(function (key) {
      if (
        userInfoState.stepNumber === 3 &&
        step3RequiredFields.includes(key) &&
        ["", null, undefined].includes(userInfo.user_obj[key])
      ) {
        userInfoContextDispatch({
          type: "MANAGE_FIELD_RELATED_ERROR_MESSAGES",
          payload: {
            name: key,
            value: ["This field is required."],
          },
        });
        hasErrorStep3 = true;
      }
    });
    return hasErrorStep3;
  };

  const handleChangeStep = (stepType) => {
    // Clear Messages
    userInfoContextDispatch({
      type: "CLEAR_ALL_ERROR_MESSAGES",
    });

    if (stepType === "next") {
      // For step 1
      if (userInfoState.stepNumber === 1 && !hasEmptyFieldStep1()) {
        userInfoContextDispatch({
          type: "ON_CHANGE_STEP",
          payload: 2,
        });
      }
      // For step 2
      if (userInfoState.stepNumber === 2 && !hasEmptyFieldStep2()) {
        userInfoContextDispatch({
          type: "ON_CHANGE_STEP",
          payload: 3,
        });
      }

      // For step 2
      if (
        userData?.user_type === "shipper" &&
        userInfoState.stepNumber === 3 &&
        !hasEmptyFieldStep2()
      ) {
        userInfoContextDispatch({
          type: "ON_CHANGE_STEP",
          payload: 4,
        });
      }
    } else if (stepType === "back") {
      userInfoContextDispatch({
        type: "ON_CHANGE_STEP",
        payload: userInfoState.stepNumber - 1,
      });
    }

  };

  const [currentTabList, setCurrentTabList] = useState();
  const [tabTimeLineWidth, setTabTimeLineWidth] = useState(0);

  useEffect(() => {
    let shiper_tab = ["Basic", "Shipper", "Card Info", "Bank"];
    let carrier_tab = ["Basic", "Carrier", "Bank"];

    if (userData?.user_type === "shipper") {
      tab_render(shiper_tab);
      let height_cal =
        (90 / (shiper_tab.length - 1)) * (userInfoState.stepNumber - 1);
      setTabTimeLineWidth(height_cal);
    } else {
      tab_render(carrier_tab);
      let height_cal =
        (90 / (carrier_tab.length - 1)) * (userInfoState.stepNumber - 1);
      setTabTimeLineWidth(height_cal);
    }

    function tab_render(tab_holder) {
      const tab_dom = tab_holder.map(
        (e, i) =>
          `<span activate-tab="${
            userInfoState.stepNumber >= i + 1
          }" >${e}</span>`
      );
      setCurrentTabList(tab_dom);
    }
  }, [userInfoState.stepNumber, userData?.user_type]);

  return (
    <ManageCompleteUserProfileContext.Provider
      value={[userInfoState, userInfoContextDispatch]}
    >
      <section id="registration-area">
        <div className="container section position-relative">
          <div className="row">
            <div className="col-lg-4 position-relative z-index-9">
              <h1 className="registration-title">Basic Information</h1>
              <div
                className="registration-time-line"
                position={userInfoState.stepNumber}
              >
                {currentTabList &&
                  currentTabList.map((htmlTags) => parse(htmlTags))}

                <div
                  className="tab-timeline"
                  style={{
                    height: tabTimeLineWidth + "%",
                  }}
                ></div>
              </div>

              <p className="registration-instruction">
                The original Poncor Dispatch recommends shippers to setup their
                accounts fully to realize the benefit of this platform
                seamlessly. Any incomplete and inaccurate accounts will delay
                your access approval, make transactions void and in some cases
                render the account inactive
              </p>
            </div>

            <div className="col-lg-8 position-relative z-index-9">
              {userInfoState.userInfoSuccessMessage !== "" ? (
                <Alert severity="success" className="mb-4">
                  <AlertTitle>Success</AlertTitle>
                  <strong>{userInfoState.userInfoSuccessMessage}</strong>
                </Alert>
              ) : (
                ""
              )}

              {userInfoState.userInfoError?.general?.length > 0 && (
                <Alert severity="error" className="mb-4">
                  <AlertTitle>Error</AlertTitle>
                  <strong>{userInfoState.userInfoError?.general}</strong>
                </Alert>
              )}

              {userInfoState.stepNumber === 1 && (
                <BasicInfo userID={userData?.id} />
              )}
              {userInfoState.stepNumber === 2 &&
                userData?.user_type === "carrier" && <CarrierInfo />}
              {userInfoState.stepNumber === 2 &&
                userData?.user_type === "shipper" && <ShipperInfo />}
              {userData?.user_type === "carrier" &&
                userInfoState.stepNumber === 3 && <BankInfo />}
              {userData?.user_type === "shipper" &&
                userInfoState.stepNumber === 4 && <BankInfo />}
              {userInfoState.stepNumber === 3 &&
                userData?.user_type === "shipper" && <CardInfo />}

              <div
                className={`back-cancel-forward-btn mt-5 ${
                  userInfoState.stepNumber === 1 && "next-btn-justify-end"
                }`}
              >
                {userData?.user_type === "carrier" &&
                  [2, 3].includes(userInfoState.stepNumber) && (
                    <Button
                      className="back-btn"
                      onClick={() => handleChangeStep("back")}
                    >
                      <i className="fa-solid fa-arrow-left"></i>Back
                    </Button>
                  )}

                {userData?.user_type === "shipper" &&
                  [2, 4].includes(userInfoState.stepNumber) && (
                    <Button
                      className="back-btn"
                      onClick={() => handleChangeStep("back")}
                    >
                      <i className="fa-solid fa-arrow-left"></i>Back
                    </Button>
                  )}

                {[1, 2].includes(userInfoState.stepNumber) && (
                  <Button
                    className="theme-btn"
                    onClick={() => handleChangeStep("next")}
                  >
                    Next
                  </Button>
                )}

                {userData?.user_type === "carrier" &&
                  userInfoState.stepNumber === 3 && (
                    <Button className="theme-btn" onClick={handleSubmitData}>
                      Finish Now
                      {isLoading && (
                        <SpinnerCircular
                          size={22}
                          thickness={180}
                          speed={130}
                          color="#fff"
                          className="btn-loader"
                          secondaryColor="rgba(57, 172, 127, 0.44)"
                        />
                      )}
                    </Button>
                  )}

                {userData?.user_type === "shipper" &&
                  userInfoState.stepNumber === 4 && (
                    <Button className="theme-btn" onClick={handleSubmitData}>
                      Finish Now
                      {isLoading && (
                        <SpinnerCircular
                          size={22}
                          thickness={180}
                          speed={130}
                          color="#fff"
                          className="btn-loader"
                          secondaryColor="rgba(57, 172, 127, 0.44)"
                        />
                      )}
                    </Button>
                  )}
              </div>
            </div>
          </div>

          <img className="registration-bg" src={RegistrationBG} alt="" />
        </div>
      </section>
    </ManageCompleteUserProfileContext.Provider>
  );
};

export default CompleteUserProfile;
